import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";

import useAuth from "src/hooks/useAuth";
import type { Id } from "src/types/id";
import type { Pos, PosDetails } from "src/types/pos";
import type { SetupState } from "src/types/pos/setupState";
import type { PosChannel } from "src/types/posChannel";
import type { ThirdServiceCategory } from "src/types/posCredential";
import { Query } from "src/types/query";
import type { RemoteShop } from "src/types/remoteShop";
import { UserRoleEnum } from "src/types/role";
import type { Competition } from "src/types/visibility/Competition";
import type { ShopListing } from "src/types/visibility/ShopListing";
import axios from "src/utils/axios";
import { filterPossToDisplay } from "src/utils/shouldDisplayEntity";

export const useQueryPointsOfSales = ({
    enabled = true,
    activatedOnly = true,
    relations = ["organization", "externalPos"],
    organizationId,
}: {
    enabled?: boolean;
    activatedOnly?: boolean;
    organizationId?: number;
    relations?: string[];
}): UseQueryResult<Pos[], unknown> => {
    const { user } = useAuth();

    return useQuery({
        queryKey: [Query.PointsOfSales, user?.id, true, organizationId, relations.join(",")],

        queryFn: async () => {
            const poss = (
                await axios.get<{ poss: Pos[] }>("/pos", {
                    params: { setupState: 0, organizationId, relations },
                })
            ).data.poss;

            return activatedOnly ? filterPossToDisplay(poss, true) : poss;
        },

        enabled,
        placeholderData: keepPreviousData,
    });
};

export const useQueryPointsOfSalesAdmin = ({
    page,
    limit,
    order,
    orderBy,
    organizationId,
}: {
    page: number;
    limit: number;
    order: string;
    orderBy: string;
    organizationId: number;
}): UseQueryResult<{ poss: Pos[]; count: number }, unknown> => {
    const { user } = useAuth();
    const displayHiddenEntities = user?.role === UserRoleEnum.SUPERADMIN;

    return useQuery({
        queryKey: [Query.PointsOfSalesSetup, user?.id, page, limit, organizationId, order, orderBy],

        queryFn: async () => {
            const { poss, count } = (
                await axios.get<{
                    poss: Pos[];
                    count: number;
                }>("/pos", {
                    params: {
                        page,
                        limit,
                        organizationId,
                        order,
                        orderBy,
                        relations: ["organization"],
                    },
                })
            ).data;
            const filteredPoss = filterPossToDisplay(poss, displayHiddenEntities);
            return {
                poss: filteredPoss,
                count,
            };
        },
    });
};

export const useQueryPointsOfSalesSetUpState = ({
    poss,
}: {
    poss: Id[];
}): UseQueryResult<{ setupState: SetupState[] }, unknown> => {
    const { user } = useAuth();

    return useQuery({
        queryKey: [Query.PointsOfSalesSetupState, user?.id, poss],

        queryFn: async () => {
            const { setupState } = (
                await axios.get<{
                    poss: Pos[];
                    setupState: SetupState[];
                    count: number;
                    organizationId: number;
                }>("/pos/setup-state", {
                    params: { poss },
                })
            ).data;
            return {
                setupState,
            };
        },
    });
};

export const useQueryPointOfSaleSetUpState = (posId: Id): UseQueryResult<SetupState, unknown> => {
    const { user } = useAuth();
    return useQuery({
        queryKey: [Query.PointOfSalesSetup, user.id, posId],
        queryFn: async () =>
            (await axios.get<{ setupState: SetupState }>(`/pos/${posId}/setup-state`)).data
                .setupState,
    });
};

export const useQueryPointOfSales = (posId: Id): UseQueryResult<Pos, unknown> => {
    const { user } = useAuth();

    return useQuery({
        queryKey: [Query.PointOfSales, user.id, posId],
        queryFn: async () => (await axios.get<{ pos: Pos }>(`/pos/${posId}`)).data.pos,
    });
};

export const useQueryPointOfSalesDetails = (
    posId: Id | undefined
): UseQueryResult<PosDetails, unknown> => {
    const { user } = useAuth();

    return useQuery({
        queryKey: [Query.PointOfSalesDetails, user.id, posId],

        queryFn: async () =>
            (
                await axios.get<{ pos: PosDetails }>(`/pos/${posId}/details`, {
                    params: { setupState: 0 },
                })
            ).data.pos,

        enabled: !!posId,
    });
};

export const useQueryPointOfSalesSetUpStateDetails = (
    posId: Id
): UseQueryResult<{ pos: PosDetails; setupState: SetupState }, unknown> => {
    const { user } = useAuth();
    return useQuery({
        queryKey: [Query.PointOfSalesSetup, user.id, posId],

        queryFn: async () =>
            (
                await axios.get<{ pos: PosDetails; setupState: SetupState }>(
                    `/pos/${posId}/details`,
                    {
                        params: { setupState: 1 },
                    }
                )
            ).data,
    });
};

export const useQueryShops = ({
    posId,
    competitorName,
    service,
    radius,
    serviceLink,
}: {
    posId: number;
    competitorName: string;
    service: string;
    radius: number;
    serviceLink: string;
}): UseQueryResult<RemoteShop[], unknown> => {
    const { user } = useAuth();

    return useQuery({
        queryKey: [Query.Shops, user.id, service, radius, `${competitorName}-${serviceLink}`],

        queryFn: async () => {
            if (!competitorName && !serviceLink) {
                return [];
            }

            const params: any = {
                posId,
                competitorName,
                serviceLink,
                service,
                radius: radius * 1000,
            };

            return (await axios.get<{ shops: RemoteShop[] }>("/visibility/shop", { params })).data
                .shops;
        },

        enabled: competitorName !== "" || serviceLink !== "",
    });
};

export const useQueryCompetitions = (posId: Id): UseQueryResult<Competition[], unknown> => {
    const { user } = useAuth();

    return useQuery({
        queryKey: [Query.Competition, user.id, posId],

        queryFn: async () =>
            (
                await axios.get<{ competition: Competition[] }>(
                    `/pos/${posId}/competition?includePos=1`
                )
            ).data.competition,
    });
};

const EXCLUDE_CHANNEL_NAME = ["onSite", "takeAway"];

export const useQueryPosChannels = (posId: Id): UseQueryResult<PosChannel[], unknown> => {
    const { user } = useAuth();

    return useQuery({
        queryKey: [Query.PosChannels, user.id, posId],

        queryFn: async () =>
            (
                await axios.get<{ channels: PosChannel[] }>(`/pos/${posId}/channel`, {
                    params: { exclude: EXCLUDE_CHANNEL_NAME },
                })
            ).data.channels,
    });
};

export const useQueryPointsOfSalesDetails = (
    enabled = true
): UseQueryResult<PosDetails[], unknown> => {
    const { user } = useAuth();
    const displayHiddenEntities = user?.role === UserRoleEnum.SUPERADMIN;

    return useQuery({
        queryKey: [Query.PointsOfSales, user?.id],

        queryFn: async () =>
            filterPossToDisplay(
                (
                    await axios.get<{ poss: PosDetails[] }>("/pos/details", {
                        params: { setupState: 0 },
                    })
                ).data.poss,
                displayHiddenEntities
            ),

        initialData: [],
        enabled,
        placeholderData: keepPreviousData,
    });
};

export function useAlreadyTargetedRemoteShop({
    shopUuid,
    service,
    posId,
}: {
    shopUuid: string;
    service: ThirdServiceCategory;
    posId: number;
}): UseQueryResult<Pos | null> {
    const { user } = useAuth();
    return useQuery({
        queryKey: [Query.AlreadyTargetedRemoteShop, user.id, shopUuid, service, posId],

        queryFn: async () => {
            const response = await axios.get<{ pos: Pos | null }>(
                "/visibility/shop-already-targeted",
                {
                    params: { shopUuid, service, posId },
                }
            );

            return response.data.pos;
        },

        enabled: !!(shopUuid && service),
    });
}
interface Cuisine {
    slug: string;
    name: string;
}

export function useQueryCuisines({
    service,
}: {
    service: ThirdServiceCategory;
}): UseQueryResult<Cuisine[] | null> {
    const { user } = useAuth();
    return useQuery({
        queryKey: [Query.Cuisines, user.id, service],

        queryFn: async () => {
            const response = await axios.get<{ cuisines: Cuisine[] | null }>(
                "/visibility/cuisine",
                {
                    params: { service },
                }
            );

            return response.data.cuisines;
        },

        enabled: !!service,
    });
}

export function useQueryBestConcurrents(
    posId: Id,
    service: ThirdServiceCategory,
    cuisines: string[]
): UseQueryResult<ShopListing[], unknown> {
    const { user } = useAuth();

    return useQuery({
        queryKey: [Query.Competition, user.id, posId, service, cuisines.join(",")],

        queryFn: async () =>
            (
                await axios.get<{ shopListing: ShopListing[] }>(
                    `/pos/${posId}/competition/best-concurrents`,
                    {
                        params: { service, cuisines },
                    }
                )
            ).data.shopListing,
    });
}
